import React, { FC, useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import classnames from 'classnames';
import Swiper from 'swiper';
import { Header } from '@src/containers/Source/Header';
import { useQueryParams } from '@src/customHooks/useQueryParams';
import { isDraft, isInactive } from '@src/util/opening';
import { DEFAULT_CURRENCY_CODE, STATIC_MEDIA_DOMAIN } from '@src/constants';
import { MetricAction, MetricContext, MetricEventSource } from '@src/metrics/metrics.enum';
import { trackEvent, trackViewCreateTargeting } from '@src/metrics';
import { NEW_FUNNEL_ROUTE, OLD_FUNNEL_ROUTE } from '@src/Routes/links';
import { CompanyProfileStateEnum, useGetTaActiveOpeningLazyQuery, useSessionQuery } from '@src/graphql/generated';
import { useAppSelector } from '@src/redux';
import { getNotice } from '@src/redux/selectors/notice';
import { QueryParams } from '@src/containers/Source/interfaces/queryParams.interface';
import { KeyboardNotice } from '@src/components/KeyboardNotice';
import { LocalStorageKeys } from '@src/enums/localStorageKeys.enum';
import { FinishCompanyProfileModal } from '@src/components/FinishCompanyProfileModal';
import { SubscribeNotice } from '@src/components/SubscribeNotice';
import { EmptyState } from './TalentsSection/EmptyState';
import { Sidebar } from './Sidebar';
import { TalentsSection } from './TalentsSection';
import { useVerifiedTalents } from './hooks/useVerifiedTalents';
import './styles.scss';

export const PipelineTalents: FC = () => {
  const excludedIds = useRef<string[]>([]);

  const { data: sessionData } = useSessionQuery();

  const { queryParams } = useQueryParams<QueryParams>();

  // const customerStatus = useAppSelector(getCustomerStatus);

  const activeOpeningId = queryParams.openingId;

  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const [swiper, setSwiper] = useState<Swiper>();

  const [isPlacesSearchOpen, setIsPlacesSearchOpen] = useState(false);

  const [swiperActiveIndex, setSwiperActiveIndex] = useState<number>(0);

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const notice = useAppSelector(getNotice);

  const recruiter = sessionData?.currentRecruiter;

  const isCompanyProfilePublished = sessionData?.company.profile.state === CompanyProfileStateEnum.PUBLISHED;

  // const companySubscription = customerStatus.planInfo.state;
  // const isSubscriptionInactive = companySubscription === CustomerCalculatedSubscriptionStateEnum.SUBSCRIPTION_INACTIVE;
  // Allowing to use the feature for all users for now
  const isInactiveSubscription = false;

  const isActionsDisabled = isInactiveSubscription || !isCompanyProfilePublished;

  const sendMetricLoadEvent = (additionalPayload?: Record<string, unknown>) => {
    trackEvent({
      context: MetricContext.SOURCE,
      interaction: MetricAction.VIEW_SOURCE_PAGE,
      payload: {
        'Opening Id': activeOpeningId || 'Discovery',
        ...additionalPayload,
      },
    });
  };

  const [getTaActiveOpening, { data: activeOpeningData, loading: isActiveOpeningLoading }] =
    useGetTaActiveOpeningLazyQuery();

  const selectedOpening = activeOpeningId ? activeOpeningData?.opening : undefined;

  const {
    feed: verifiedTalentsFeed,
    isInitialFetchLoading: isVerifiedTalentsFeedInitialFetchLoading,
    isFetchMoreLoading: isVerifiedTalentsFeedFetchMoreLoading,
    hasMoreResults: hasMoreVerifiedTalentsResults,
    loadMore: loadMoreVerifiedTalentsList,
    removeVerifiedUser: removeVerifiedUserFromList,
  } = useVerifiedTalents({
    excludedUserIds: excludedIds.current,
    isActiveOpeningLoading: isActiveOpeningLoading,
    onLoad: totalCount => {
      sendMetricLoadEvent({
        'Recommended Users Count': totalCount,
      });
    },
    openingId: queryParams.openingId,
    recruiterCurrencyCode: recruiter?.country?.currency || DEFAULT_CURRENCY_CODE,
    swiperActiveIndex: swiperActiveIndex,
  });

  const onDiscardTalent = (userId: string) => {
    excludedIds.current.push(userId);

    removeVerifiedUserFromList(userId);
  };

  const onConfirmTalent = (userId: string) => {
    excludedIds.current.push(userId);
  };

  const onUserSelect = (index: number) => {
    swiper?.slideTo(index);
  };

  useEffect(() => {
    if (selectedOpening?.state && (isInactive(selectedOpening?.state) || isDraft(selectedOpening?.state))) {
      sendMetricLoadEvent();
    }

    if (activeOpeningId) {
      getTaActiveOpening({
        variables: {
          id: activeOpeningId,
        },
      });
    }
  }, [activeOpeningId]);

  const isBackDropShown = isSearchOpen || isPlacesSearchOpen || isFiltersOpen;

  const renderTalentSection = () => {
    if (activeOpeningId && isInactive(selectedOpening?.state)) {
      return (
        <div className="pipeline-talents__empty-state-wrapper">
          <EmptyState
            buttonLabel="Create a new opening"
            buttonProps={{
              isLink: true,
              onClick: () =>
                trackViewCreateTargeting({
                  'Event Source': MetricEventSource.INACTIVE_OPENINGS_EMPTY_STATE,
                  'Previous Context': MetricContext.SOURCE,
                }),
              to: NEW_FUNNEL_ROUTE,
            }}
            imageUrl={`${STATIC_MEDIA_DOMAIN}/images/ta-inactive-opening-empty-state.png`}
            text="Create an active position to start sourcing talent."
            title="This position is inactive"
          />
        </div>
      );
    } else if (activeOpeningId && isDraft(selectedOpening?.state)) {
      return (
        <div className="pipeline-talents__empty-state-wrapper">
          <EmptyState
            buttonLabel="Review and publish"
            buttonProps={{
              isLink: true,
              onClick: () =>
                trackViewCreateTargeting({
                  'Event Source': MetricEventSource.DRAFT_OPENINGS_EMPTY_STATE,
                  'Previous Context': MetricContext.SOURCE,
                }),
              params: { openingId: queryParams?.openingId },
              to: OLD_FUNNEL_ROUTE,
            }}
            imageUrl={`${STATIC_MEDIA_DOMAIN}/images/ta-inactive-opening-empty-state.png`}
            text="Publish the position to start sourcing talent."
            title="This is a draft position"
          />
        </div>
      );
    } else {
      return (
        <TalentsSection
          defaultPitchMessage={selectedOpening?.pitch || undefined}
          hasMoreVerifiedTalentsResults={hasMoreVerifiedTalentsResults}
          isActiveOpeningLoading={isActiveOpeningLoading}
          isKeyboardShortcutsDisable={isBackDropShown}
          isSubscriptionInactive={isActionsDisabled}
          isVerifiedTalentsFeedFetchMoreLoading={isVerifiedTalentsFeedFetchMoreLoading}
          isVerifiedTalentsFeedInitialFetchLoading={isVerifiedTalentsFeedInitialFetchLoading}
          onTalentConfirm={talentId => onConfirmTalent(talentId)}
          onTalentDiscard={talentId => onDiscardTalent(talentId)}
          openingState={selectedOpening?.state}
          openingTitle={selectedOpening?.title || undefined}
          setSwiper={setSwiper}
          setSwiperActiveIndex={setSwiperActiveIndex}
          swiper={swiper}
          swiperActiveIndex={swiperActiveIndex}
          verifiedTalentsFeed={verifiedTalentsFeed}
        />
      );
    }
  };

  return (
    <>
      {isInactiveSubscription && <SubscribeNotice className="pipeline-talents__subscribe-notice" />}

      {!isInactiveSubscription && !isCompanyProfilePublished && (
        <FinishCompanyProfileModal className="pipeline-talents__finish-profile-modal" />
      )}

      <div
        className={classnames(
          'pipeline-talents',
          !!notice && 'pipeline-talents--notice-active',
          isActionsDisabled && 'pipeline-talents--blur',
        )}
      >
        <Helmet>
          <title>Sourcing</title>
        </Helmet>

        <div className="pipeline-talents__header">
          <Header activeOpeningId={queryParams?.openingId} isActiveOpeningLoading={isActiveOpeningLoading} />
        </div>

        <div className="pipeline-talents__body">
          <div className="pipeline-talents__left-column">
            <div className="pipeline-talents__openings-wrapper">
              <Sidebar
                hasMoreVerifiedTalentsListResults={hasMoreVerifiedTalentsResults}
                isFiltersOpen={isFiltersOpen}
                isPlacesSearchOpen={isPlacesSearchOpen}
                isSearchOpen={isSearchOpen}
                isSubscriptionInactive={isActionsDisabled}
                isVerifiedTalentsListFeedFetchMoreLoading={isVerifiedTalentsFeedFetchMoreLoading}
                isVerifiedTalentsListFeedInitialFetchLoading={isVerifiedTalentsFeedInitialFetchLoading}
                loadMoreVerifiedTalentsList={loadMoreVerifiedTalentsList}
                onUserSelect={index => index !== undefined && onUserSelect(index)}
                setIsFiltersOpen={setIsFiltersOpen}
                setIsPlacesSearchOpen={setIsPlacesSearchOpen}
                setIsSearchOpen={setIsSearchOpen}
                verifiedTalentsListFeed={verifiedTalentsFeed}
              />
            </div>
          </div>

          <div className="pipeline-talents__right-column">
            <div className="pipeline-talents__talents-wrapper">{renderTalentSection()}</div>
          </div>
        </div>

        <div
          className={classnames('pipeline-talents__backdrop', isBackDropShown && 'pipeline-talents__backdrop--shown')}
        />

        <KeyboardNotice localStorageKey={LocalStorageKeys.KEYBOARD_SHORTCUTS_NOTICE_SEEN_COUNT} />
      </div>
    </>
  );
};
